/**
 * SPIN TENET — Control Plane RTK Query Service
 *
 * Manages RESTful control plane operations over HTTPS:
 * Projects, investigations, run manifests, job statuses.
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { Project, Investigation, RunSummary, RunManifest } from '@contracts/index';

export interface HealthStatus {
  status: string;
  service: string;
  hostname: string;
  python: string;
  platform?: string;
  project_root?: string;
}

const CONTROL_PLANE_URL = ((import.meta as any).env?.VITE_SPIN_TENET_CONTROL_PLANE_URL || 'https://desktop-pcu50.tail512eca.ts.net').replace(/\/$/, '');

export const controlPlaneApi = createApi({
  reducerPath: 'controlPlaneApi',
  baseQuery: fetchBaseQuery({ baseUrl: `${CONTROL_PLANE_URL}/api/v1` }),
  tagTypes: ['Project', 'Investigation', 'Run', 'Manifest', 'Health'],
  endpoints: (builder) => ({
    getHealth: builder.query<HealthStatus, void>({
      query: () => ({
        url: '/health',
        method: 'GET',
      }),
      providesTags: ['Health']
    }),
    getProjects: builder.query<Project[], void>({
      query: () => '/projects',
      providesTags: ['Project']
    }),
    getProjectById: builder.query<Project, string>({
      query: (id) => `/projects/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Project', id }]
    }),
    getInvestigations: builder.query<Investigation[], string>({
      query: (projectId) => `/projects/${projectId}/investigations`,
      providesTags: ['Investigation']
    }),
    getRuns: builder.query<RunSummary[], string>({
      query: (investigationId) => `/investigations/${investigationId}/runs`,
      providesTags: ['Run']
    }),
    getRunManifest: builder.query<RunManifest, string>({
      query: (manifestUri) => manifestUri,
      providesTags: (_result, _error, uri) => [{ type: 'Manifest', id: uri }]
    })
  })
});

export const {
  useGetHealthQuery,
  useGetProjectsQuery,
  useGetProjectByIdQuery,
  useGetInvestigationsQuery,
  useGetRunsQuery,
  useGetRunManifestQuery
} = controlPlaneApi;
